import React, { useState } from 'react';
import { collection, addDoc } from 'firebase/firestore';
import { db, auth } from './firebase';
import {
  EnvelopeIcon,
  ChatBubbleLeftEllipsisIcon,
  DevicePhoneMobileIcon,
  QuestionMarkCircleIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline';

const Contato = () => {
  const [formData, setFormData] = useState({ name: '', email: '', subject: 'Suporte Técnico', message: '' });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const contactMethods = [
    {
      name: 'E-mail Oficial',
      value: process.env.REACT_APP_CONTACT_EMAIL || 'contato@traderflix.online',
      icon: EnvelopeIcon,
      link: `mailto:${process.env.REACT_APP_CONTACT_EMAIL || 'contato@traderflix.online'}`,
      color: 'blue'
    },
    {
      name: 'Comunidade Telegram',
      value: process.env.REACT_APP_TELEGRAM_HANDLE || '@traderflix',
      icon: ChatBubbleLeftEllipsisIcon,
      link: process.env.REACT_APP_TELEGRAM_LINK || 'https://t.me/traderflix',
      color: 'sky'
    },
    {
      name: 'Suporte WhatsApp',
      value: process.env.REACT_APP_WHATSAPP_DISPLAY || '+55 XX XXXXX-XXXX',
      icon: DevicePhoneMobileIcon,
      link: process.env.REACT_APP_WHATSAPP_LINK || 'https://wa.me/0000000000000',
      color: 'emerald'
    }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.message || !formData.name) return;

    setLoading(true);
    try {
      await addDoc(collection(db, 'tickets'), {
        ...formData,
        userId: auth.currentUser?.uid,
        userEmail: auth.currentUser?.email,
        status: 'Pendente',
        createdAt: new Date().toISOString()
      });
      setSuccess(true);
      setFormData({ name: '', email: '', subject: 'Suporte Técnico', message: '' });
      setTimeout(() => setSuccess(false), 5000);
    } catch (err) {
      console.error("Erro ao enviar chamado:", err);
      alert("Erro ao enviar chamado. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-12 animate-fade-in">
      {/* Header */}
      <div className="text-center max-w-2xl mx-auto space-y-4">
        <h1 className="text-4xl font-bold text-white italic uppercase tracking-tighter">Centro de Suporte <span className="text-emerald-500">TraderFlix</span></h1>
        <p className="text-gray-400 font-medium">
          Estamos aqui para garantir que sua jornada no mercado financeiro seja fluida.
          Escolha o canal de sua preferência ou abra um chamado interno.
        </p>
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        {/* Contact Cards */}
        <div className="lg:col-span-1 space-y-6">
          {contactMethods.map((method, idx) => (
            <a
              key={idx}
              href={method.link}
              target="_blank"
              rel="noopener noreferrer"
              className="block group bg-white/5 border border-white/10 p-6 rounded-[2rem] hover:bg-white/[0.08] hover:border-emerald-500/30 transition-all"
            >
              <div className="p-3 bg-white/5 rounded-2xl w-fit mb-4 group-hover:scale-110 transition-transform">
                <method.icon className="w-6 h-6 text-white" />
              </div>
              <p className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-1">{method.name}</p>
              <p className="text-lg font-bold text-white group-hover:text-emerald-400 transition-colors">{method.value}</p>
            </a>
          ))}

          <div className="bg-emerald-500/5 border border-emerald-500/10 p-8 rounded-[2.5rem] space-y-4">
            <div className="flex items-center gap-3 text-emerald-400">
              <ShieldCheckIcon className="w-6 h-6" />
              <span className="font-bold uppercase text-xs tracking-widest">Garantia de Resposta</span>
            </div>
            <p className="text-xs text-gray-400 leading-relaxed font-bold italic">
              Nossa equipe responde em média em até <span className="text-white">2 dias úteis</span>.
            </p>
          </div>
        </div>

        {/* Support Form */}
        <div className="lg:col-span-2 bg-white/5 border border-white/10 rounded-[2.5rem] p-10 backdrop-blur-md relative overflow-hidden">
          {success && (
            <div className="absolute inset-0 bg-emerald-600/90 backdrop-blur-xl z-20 flex flex-col items-center justify-center text-center p-10 animate-fade-in">
              <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mb-6 shadow-2xl">
                <ShieldCheckIcon className="w-12 h-12 text-emerald-600" />
              </div>
              <h3 className="text-3xl font-black text-white italic uppercase mb-2">Chamado Enviado!</h3>
              <p className="text-emerald-100 font-bold uppercase text-xs tracking-widest">Protocolo recebido com sucesso. Responderemos em breve.</p>
            </div>
          )}

          <div className="flex items-center gap-3 mb-8">
            <QuestionMarkCircleIcon className="w-8 h-8 text-emerald-500" />
            <h2 className="text-2xl font-bold text-white italic uppercase">Abra um chamado interno</h2>
          </div>

          <form className="grid grid-cols-1 md:grid-cols-2 gap-6" onSubmit={handleSubmit}>
            <div className="space-y-2">
              <label className="text-xs font-bold text-gray-500 uppercase tracking-wider ml-4">Nome Completo</label>
              <input
                type="text"
                required
                className="w-full bg-black/20 border border-white/10 rounded-2xl p-4 text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/50 transition-all font-medium placeholder:text-gray-700"
                placeholder="Ex: João Silva"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <label className="text-xs font-bold text-gray-500 uppercase tracking-wider ml-4">Assunto</label>
              <select
                className="w-full bg-black/20 border border-white/10 rounded-2xl p-4 text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/50 transition-all font-medium appearance-none"
                value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
              >
                <option value="Suporte Técnico" className="bg-[#020617]">Suporte Técnico</option>
                <option value="Dúvida sobre Aula" className="bg-[#020617]">Dúvida sobre Aula</option>
                <option value="Financeiro" className="bg-[#020617]">Financeiro</option>
                <option value="Feedback" className="bg-[#020617]">Feedback</option>
              </select>
            </div>
            <div className="md:col-span-2 space-y-2">
              <label className="text-xs font-bold text-gray-500 uppercase tracking-wider ml-4">Sua Mensagem</label>
              <textarea
                rows="5"
                required
                className="w-full bg-black/20 border border-white/10 rounded-2xl p-6 text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/50 transition-all resize-none font-medium placeholder:text-gray-700"
                placeholder="Descreva detalhadamente como podemos te ajudar..."
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              ></textarea>
            </div>
            <div className="md:col-span-2">
              <button
                type="submit"
                disabled={loading}
                className="w-full py-5 bg-emerald-600 text-white font-black rounded-2xl hover:bg-emerald-500 transition-all active:scale-[0.98] shadow-xl shadow-emerald-600/20 flex items-center justify-center gap-3 uppercase italic"
              >
                {loading ? <div className="w-6 h-6 border-4 border-white border-t-transparent rounded-full animate-spin" /> : 'Enviar Chamado Agora'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Contato;
